import { object, string, mixed, array, number, boolean } from 'yup'
import { buildHandInProjectName } from './ProjectDataForm/utils'
import { SHOW_CLICKUP_LINK } from './constants'

const HAND_IN_PROJECT_NAME_TOO_LONG = 'Projektname für Abgabe darf maximal 50 Zeichen haben'

// The native accept attribute only filters the file picker, and errors set by
// hand are wiped by the next resolver run -- so the file type is validated
// here, where it survives until submit.
const fileOfType = (extensions: string[]) =>
  mixed().test('file-type', 'Falscher Dateityp', value => {
    if (!value) return true
    const fileName = (value as File).name ?? ''
    return extensions.some(extension => fileName.toLowerCase().endsWith(extension))
  })

const addressDataSchema = object({
  street: string().default(''),
  city: string().default(''),
  zip_code: string().default(''),
})

const clickupInfoSchema = object({
  clickup_project_no: string()
    .matches(/^\d{4}$/, 'Must be a 4-digit number')
    .required('Clickup project number is required for customer projects'),
  clickup_project_name: string().required('Clickup project name is required for customer projects'),
})

const testProjectClickupSchema = object({
  clickup_project_no: string().default(''),
  clickup_project_name: string().default(''),
})

export const baseProjectSchema = object({
  name: string().required().default('').min(5, 'Name muss mindestes 5 Zeichen haben'),
  description: string().default(''),
  owner_name: string().default(''),
  owner_surname: string().default(''),
  owner_address: string().default(''),
  // Deprecated names, still part of the payload so edits round-trip; the
  // server freezes them. The pickers write the two fields below instead.
  responsible_engineer_name: string().default(''),
  processor_name: string().default(''),
  // Planungsverantwortung: a responsible member of the organisation (R17).
  responsible_user_guid: string().default(''),
  // Bearbeitung: only honoured at creation; later changes are admin-only
  // reassignment via the project list (R12).
  primary_editor_guid: string().default(''),
  construction_company_name: string().default(''),
  // Either composed from the description and the name (see
  // buildHandInProjectName) or typed, depending on the switch below; the
  // bound matches the backend column.
  hand_in_project_name: string().default('').max(50, HAND_IN_PROJECT_NAME_TOO_LONG),
  generate_hand_in_project_name: boolean().default(false),
  address: addressDataSchema,
  clickup_info: mixed().when('addClickupData', {
    // Only the Clickup box makes its number and name mandatory. With the box
    // hidden the number stands on its own as the optional Projektnummer.
    is: (addClickupData: boolean) => addClickupData && SHOW_CLICKUP_LINK,
    then: () => clickupInfoSchema,
    otherwise: () => testProjectClickupSchema,
  }),
  tags: array().of(string().required()).default([]),
  addClickupData: boolean().default(false),
}).test('composed-hand-in-project-name-length', HAND_IN_PROJECT_NAME_TOO_LONG, function (values) {
  // The composed name is spliced into the payload at submit time, never
  // typed into the (unregistered) preview field -- so the max() above never
  // sees it and the bound has to be checked against its ingredients here.
  // A typed name goes into the registered field instead and is capped by the
  // max() above.
  if (!values.generate_hand_in_project_name) return true
  if (buildHandInProjectName(values.description, values.name).length <= 50) return true
  return this.createError({
    path: 'hand_in_project_name',
    message: HAND_IN_PROJECT_NAME_TOO_LONG,
  })
})

export const createProjectSchema = baseProjectSchema.concat(
  object({
    start_mode: string().oneOf(['ifc', 'empty', 'json']).default('ifc'),
    create_empty_model: boolean().default(false),
    storey_heights: array()
      .of(number().positive('Stockwerkshöhe muss positiv sein').required())
      .when('start_mode', {
        is: 'empty',
        then: schema => schema.default([2.95]).notRequired(),
        otherwise: schema => schema.default([]),
      }),
    model: mixed().when('start_mode', {
      is: 'json',
      then: () => fileOfType(['.json']).required('Modell ist erforderlich'),
      otherwise: () => mixed().notRequired(),
    }),
    // Optional on purpose: creating a project without an IFC file is what
    // starts it out empty (see deriveModelStart in ProjectDataForm).
    ifc_file: fileOfType(['.ifc']),
  }),
)
